import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:4000';

const TYPE_BADGE = {
  auction: { label:'🔨 Auction', color:'#FF8A65' },
  barter:  { label:'🔄 Barter',  color:'#9C27B0' },
  rent:    { label:'📅 Rental',  color:'#2196F3' },
  free:    { label:'🆓 Free',    color:'#4CAF50' },
  sale:    { label:'💰 Sale',    color:'#4CAF50' },
};

export default function LiveBrowsePage() {
  const { user }  = useAuth();
  const navigate  = useNavigate();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [sortBy, setSortBy]     = useState('viewers');

  const fetchSessions = () =>
    API.get('/live/sessions').then(r => setSessions(r.data.sessions || [])).catch(() => {});

  useEffect(() => {
    fetchSessions().finally(() => setLoading(false));
    const interval = setInterval(fetchSessions, 15000);
    return () => clearInterval(interval);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q
      ? sessions.filter(s =>
          s.title?.toLowerCase().includes(q) ||
          `${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
        )
      : sessions;

    if (sortBy === 'viewers') {
      list = [...list].sort((a, b) => (b.viewer_count || 0) - (a.viewer_count || 0));
    } else {
      list = [...list].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
    }
    return list;
  }, [sessions, search, sortBy]);

  return (
    <div style={{ maxWidth:1080, margin:'0 auto', padding:'40px 24px 80px' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:28 }}>
        <div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:32, fontWeight:700, marginBottom:6 }}>🔴 Live Now</h1>
          <p style={{ fontSize:15, color:'var(--text-soft)' }}>
            Watch sellers showcase items in real-time.
            {sessions.length > 0 && <span style={{ marginLeft:8, fontWeight:600, color:'var(--cherry)' }}>{sessions.length} live</span>}
          </p>
        </div>
        {user && (
          <button className="btn btn-cherry" onClick={() => navigate('/live/go')}>
            🎙️ Go Live
          </button>
        )}
      </div>

      {/* Search + sort bar */}
      {(sessions.length > 0 || search) && (
        <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:220, position:'relative' }}>
            <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:14, opacity:.5 }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search streams or sellers..."
              style={{ width:'100%', paddingLeft:36, paddingRight:12, height:40, border:'1px solid var(--border)', borderRadius:10, fontSize:14, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ display:'flex', gap:6 }}>
            {[['viewers','Most Viewers'], ['newest','Just Started']].map(([val, label]) => (
              <button key={val} onClick={() => setSortBy(val)}
                style={{ padding:'0 16px', height:40, borderRadius:10, border:`1px solid ${sortBy===val?'var(--cherry)':'var(--border)'}`, background: sortBy===val?'var(--cherry)':'white', color: sortBy===val?'white':'var(--text-primary)', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:60, color:'var(--text-soft)' }}>Loading streams...</div>
      ) : filtered.length === 0 && search ? (
        <div style={{ textAlign:'center', padding:'60px 40px', background:'var(--cream)', borderRadius:20 }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🔍</div>
          <p style={{ color:'var(--text-soft)' }}>No streams match "{search}"</p>
          <button className="btn btn-ghost btn-sm" style={{ marginTop:12 }} onClick={() => setSearch('')}>Clear search</button>
        </div>
      ) : sessions.length === 0 ? (
        <div style={{ textAlign:'center', padding:'80px 40px', background:'var(--cream)', borderRadius:20 }}>
          <div style={{ fontSize:64, marginBottom:16 }}>📺</div>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, marginBottom:8 }}>No live streams right now</h2>
          <p style={{ color:'var(--text-soft)', marginBottom:24 }}>Check back soon, or be the first to go live today.</p>
          {user && <button className="btn btn-cherry" onClick={() => navigate('/live/go')}>🔴 Start Streaming</button>}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:20 }}>
          {filtered.map(s => (
            <div key={s.id} onClick={() => navigate(`/live/${s.room_name}`)}
              style={{ background:'white', border:'1px solid var(--border)', borderRadius:16, overflow:'hidden', cursor:'pointer', transition:'transform .15s, box-shadow .15s' }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='var(--shadow-md)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>

              {/* Thumbnail */}
              <div style={{ height:180, background:'#1a1a2e', position:'relative', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {s.featured_image
                  ? <img src={`${API_BASE}${s.featured_image}`} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', opacity:.6 }} />
                  : <div style={{ fontSize:48, opacity:.3 }}>📺</div>
                }

                <div style={{ position:'absolute', top:12, left:12, display:'flex', gap:6, alignItems:'center' }}>
                  <div style={{ background:'#E53935', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:800, color:'white', letterSpacing:'1px' }}>● LIVE</div>
                  {s.featured_type && TYPE_BADGE[s.featured_type] && (
                    <div style={{ background: TYPE_BADGE[s.featured_type].color, borderRadius:6, padding:'3px 8px', fontSize:11, fontWeight:700, color:'white' }}>
                      {TYPE_BADGE[s.featured_type].label}
                    </div>
                  )}
                </div>

                {s.viewer_count > 0 && (
                  <div style={{ position:'absolute', top:12, right:12, background:'rgba(0,0,0,.7)', borderRadius:6, padding:'3px 10px', fontSize:12, color:'white' }}>👥 {s.viewer_count}</div>
                )}

                {s.featured_title && (
                  <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'linear-gradient(transparent,rgba(0,0,0,.8))', padding:'20px 14px 12px', color:'white' }}>
                    <div style={{ fontSize:11, opacity:.7, marginBottom:2 }}>NOW FEATURING</div>
                    <div style={{ fontWeight:700, fontSize:14 }}>{s.featured_title}</div>
                    {s.featured_type === 'auction'
                      ? <div style={{ color:'#FF8A65', fontWeight:800, fontSize:15 }}>Bid from ${parseFloat(s.featured_starting_bid || s.featured_price || 0).toLocaleString()}</div>
                      : s.featured_type === 'free'
                        ? <div style={{ color:'#4CAF50', fontWeight:800, fontSize:15 }}>FREE</div>
                        : s.featured_type === 'barter'
                          ? <div style={{ color:'#CE93D8', fontWeight:700, fontSize:14 }}>Barter offer</div>
                          : <div style={{ color:'#4CAF50', fontWeight:800, fontSize:16 }}>${parseFloat(s.featured_price || 0).toLocaleString()}</div>
                    }
                  </div>
                )}
              </div>

              {/* Info */}
              <div style={{ padding:'14px 16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                  {s.avatar_url
                    ? <img src={`${API_BASE}${s.avatar_url}`} alt="" style={{ width:32, height:32, borderRadius:'50%', objectFit:'cover' }} />
                    : <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--cherry)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700 }}>{(s.first_name||'?')[0]}</div>
                  }
                  <div>
                    <div style={{ fontWeight:700, fontSize:14 }}>{s.first_name} {s.last_name}</div>
                    {s.rating_avg > 0 && <div style={{ fontSize:11, color:'var(--text-soft)' }}>⭐ {parseFloat(s.rating_avg).toFixed(1)}</div>}
                  </div>
                </div>
                <div style={{ fontWeight:600, fontSize:15 }}>{s.title}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
